import { formatDateNumberShort } from '@seen/core/dates'
import { featureFlags } from '@seen/core/featureFlags'
import type { ExtendedMedicationRequest } from '@seen/core/models/medicationRequest'
import type { Participant } from '@seen/core/models/patients/participant'
import { cn, DotSeparator } from '@seen/ui'
import { IconPill, IconPrescription, IconPrinter } from '@tabler/icons-react'
import { type JSX, useCallback, useMemo, useRef } from 'react'
import { DropdownMenu } from '#src/components/core/dropdown-menu/DropdownMenu.tsx'
import { DropdownMenuContent } from '#src/components/core/dropdown-menu/DropdownMenuContent.tsx'
import { DropdownMenuTrigger } from '#src/components/core/dropdown-menu/DropdownMenuTrigger.tsx'
import { HoverCard } from '#src/components/core/hover-card/HoverCard.tsx'
import { HoverCardContent } from '#src/components/core/hover-card/HoverCardContent.tsx'
import { HoverCardTrigger } from '#src/components/core/hover-card/HoverCardTrigger.tsx'
import { TextLink } from '#src/components/core/TextLink.tsx'
import { MedicationDeliveryPreferenceField } from '#src/components/medications/MedicationDeliveryPreferenceField.tsx'
import { MedicationHistoryTooltip } from '#src/components/medications/MedicationHistoryTooltip.tsx'
import { MedicationName } from '#src/components/medications/MedicationName.tsx'
import { MedicationsPrintFaxMenuItems } from '#src/components/medications/MedicationsPrintFaxMenuItems.tsx'
import { MedicationsPrintFaxModal } from '#src/components/medications/MedicationsPrintFaxModal.tsx'
import {
  FacesheetCard,
  FacesheetCardActionButton,
  FacesheetCardActions,
  FacesheetCardCollapseTrigger,
  FacesheetCardContent,
  FacesheetCardCount,
  FacesheetCardHeader,
  FacesheetCardIcon,
  FacesheetCardLabel,
  FacesheetCardTitle,
} from '#src/components/patient/detail-content/facesheet/index.ts'
import {
  FACESHEET_CARD_ROW_CLASS,
  FacesheetCardSection,
  FacesheetCardSections,
} from '#src/components/patient/detail-content/FacesheetCardSection.tsx'
import {
  MEDICATION_HOVER_CARD_WIDTH,
  useAdaptiveHoverCardPlacement,
} from '#src/hooks/useAdaptiveHoverCardPlacement.ts'
import {
  type GroupedMedication,
  useCurrentMedications,
} from '#src/hooks/useCurrentMedications.ts'
import { useElationDraftPrescription } from '#src/hooks/useElationDraftPrescription.ts'
import { useElationPrescribe } from '#src/hooks/useElationPrescribe.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import {
  combineDraftIdsByThread,
  getMedicationThreadId,
  medicationForDraftOpen,
  useMedicationDraftsByThread,
} from '#src/hooks/useMedicationDraftsByThread.ts'
import { useMedicationsPrintFax } from '#src/hooks/useMedicationsPrintFax.ts'
export interface MedicationsCardProps {
  participant: Participant
}

export function MedicationsCard({
  participant,
}: MedicationsCardProps): JSX.Element {
  const {
    activeRxMedications,
    activeOtcMedications,
    temporaryRxMedications,
    temporaryOtcMedications,
    discontinuedMedications,
  } = useCurrentMedications(participant.reference)

  const {
    openPrescribe,
    canPrescribe,
    isLoading: isPrescribeLoading,
  } = useElationPrescribe(participant.id)
  const {
    createDraftPrescription,
    openDraftPrescription,
    draftingMedicationIds,
    optimisticDraftIdsByThreadId,
    canDraft,
  } = useElationDraftPrescription(participant.id, {
    surface: 'medications_card',
  })
  const { draftByThreadId } = useMedicationDraftsByThread(participant.reference)

  const printFaxEnabled = useFeatureFlag(featureFlags.medicationsCardPrintFax)
  const groupByElationThread = useFeatureFlag(
    featureFlags.medicationGroupByElationThread
  )

  const totalCount =
    activeRxMedications.length +
    activeOtcMedications.length +
    temporaryRxMedications.length +
    temporaryOtcMedications.length
  const hasBody = totalCount > 0 || discontinuedMedications.length > 0
  const openStampedDraft = useCallback(
    (draftId: number | string, medication?: ExtendedMedicationRequest) => {
      openDraftPrescription(
        draftId,
        medicationForDraftOpen({
          draftId,
          medication,
          draftsByThreadId: draftByThreadId,
        })
      )
    },
    [draftByThreadId, openDraftPrescription]
  )

  const draftIdsByThreadId = useMemo(
    () =>
      combineDraftIdsByThread({
        serverDraftsByThreadId: draftByThreadId,
        optimisticDraftIdsByThreadId,
      }),
    [draftByThreadId, optimisticDraftIdsByThreadId]
  )

  const renderMedication = (medication: GroupedMedication): JSX.Element => (
    <MedicationItem
      key={
        groupByElationThread
          ? (medication.id ?? medication.medicationDisplay)
          : (medication.medicationCode ?? medication.medicationDisplay)
      }
      medication={medication}
      participant={participant}
      onDraftPrescription={canDraft ? createDraftPrescription : undefined}
      onOpenDraftPrescription={openStampedDraft}
      draftIdsByThreadId={draftIdsByThreadId}
      draftingMedicationIds={draftingMedicationIds}
    />
  )

  return (
    <FacesheetCard collapsible={hasBody} rumName="medications">
      <FacesheetCardHeader>
        <FacesheetCardTitle>
          <FacesheetCardIcon>
            <IconPill />
          </FacesheetCardIcon>
          <FacesheetCardLabel>
            <TextLink to={`${participant.relativeUrl}/medications`}>
              Medications
            </TextLink>
          </FacesheetCardLabel>
          <FacesheetCardCollapseTrigger />
        </FacesheetCardTitle>
        <FacesheetCardActions>
          <FacesheetCardCount>{totalCount}</FacesheetCardCount>
          {printFaxEnabled && (
            <MedicationsPrintFaxButton
              participant={participant}
              rxMedications={activeRxMedications}
              otcMedications={activeOtcMedications}
            />
          )}
          {canPrescribe && (
            <FacesheetCardActionButton
              aria-label="Prescribe"
              onClick={openPrescribe}
              loading={isPrescribeLoading}
            >
              <IconPrescription />
            </FacesheetCardActionButton>
          )}
        </FacesheetCardActions>
      </FacesheetCardHeader>
      <MedicationDeliveryPreferenceField
        participant={participant}
        className="w-full"
        size="card"
      />
      {hasBody && (
        <FacesheetCardContent>
          <FacesheetCardSections className="text-xs">
            {[
              {
                title: 'Active',
                medications: activeRxMedications,
                dotClassName: 'bg-emerald-500',
                defaultOpen: true,
              },
              {
                title: 'Active OTC',
                medications: activeOtcMedications,
                dotClassName: 'bg-emerald-500',
                defaultOpen: true,
              },
              {
                title: 'Temporary',
                medications: temporaryRxMedications,
                dotClassName: 'bg-zinc-400',
                defaultOpen: false,
              },
              {
                title: 'Temporary OTC',
                medications: temporaryOtcMedications,
                dotClassName: 'bg-zinc-400',
                defaultOpen: false,
              },
              {
                title: 'Discontinued',
                medications: discontinuedMedications,
                dotClassName: 'bg-zinc-400',
                defaultOpen: false,
              },
            ].flatMap((section) =>
              section.medications.length > 0
                ? [
                    <FacesheetCardSection
                      key={section.title}
                      title={section.title}
                      count={section.medications.length}
                      dotClassName={section.dotClassName}
                      collapsible
                      defaultOpen={section.defaultOpen}
                    >
                      {section.medications.map(renderMedication)}
                    </FacesheetCardSection>,
                  ]
                : []
            )}
          </FacesheetCardSections>
        </FacesheetCardContent>
      )}
    </FacesheetCard>
  )
}

function MedicationsPrintFaxButton({
  participant,
  rxMedications,
  otcMedications,
}: {
  participant: Participant
  rxMedications: GroupedMedication[]
  otcMedications: GroupedMedication[]
}): JSX.Element {
  const printFax = useMedicationsPrintFax({
    participant,
    rxMedications,
    otcMedications,
  })

  return (
    <>
      <DropdownMenu
        open={printFax.menuOpen}
        onOpenChange={printFax.onMenuOpenChange}
      >
        <DropdownMenuTrigger asChild>
          <FacesheetCardActionButton
            aria-label="Print or fax medications"
            loading={printFax.isPrinting}
            disabled={printFax.isPrinting}
            className="data-[state=open]:bg-stone-100"
          >
            <IconPrinter />
          </FacesheetCardActionButton>
        </DropdownMenuTrigger>
        {printFax.menuOpen && (
          <DropdownMenuContent align="end">
            <MedicationsPrintFaxMenuItems
              onPrint={printFax.handlePrint}
              onFax={printFax.handleFax}
              onDismiss={printFax.dismissMenu}
              isPrinting={printFax.isPrinting}
            />
          </DropdownMenuContent>
        )}
      </DropdownMenu>
      <MedicationsPrintFaxModal faxModal={printFax.faxModal} />
    </>
  )
}

function MedicationItem({
  medication,
  participant,
  onDraftPrescription,
  onOpenDraftPrescription,
  draftIdsByThreadId,
  draftingMedicationIds,
}: {
  medication: GroupedMedication
  participant: Participant
  onDraftPrescription?: (
    medication: GroupedMedication['medicationRequests'][number],
    orderType: 'Refill'
  ) => void
  onOpenDraftPrescription: (
    draftId: number | string,
    medication?: GroupedMedication['medicationRequests'][number]
  ) => void
  draftIdsByThreadId?: ReadonlyMap<string, number | string>
  draftingMedicationIds?: ReadonlySet<string>
}): JSX.Element {
  const actionRequest =
    medication.medicationRequests.find(
      (request) => request.id === medication.id
    ) ?? medication.medicationRequests[0]
  const medThreadId = getMedicationThreadId(actionRequest ?? {})
  const draftId = medThreadId ? draftIdsByThreadId?.get(medThreadId) : undefined
  const isCreatingDraft =
    !!actionRequest && draftingMedicationIds?.has(actionRequest.id)

  const date = medication.lastOrderedDate ?? medication.startDate
  const viewportLayout = useFeatureFlag(featureFlags.hoverCardViewportLayout)
  const label = (
    <MedicationName
      display={medication.medicationDisplay}
      doseSummary={medication.doseSummary}
    />
  )
  const adaptivePlacement = useFeatureFlag(
    featureFlags.hoverCardAdaptivePlacement
  )
  const rowRef = useRef<HTMLParagraphElement>(null)
  const {
    side: adaptiveSide,
    sideOffset: adaptiveSideOffset,
    onOpenChange,
    cardRef,
  } = useAdaptiveHoverCardPlacement(rowRef, MEDICATION_HOVER_CARD_WIDTH)

  return (
    <HoverCard
      openDelay={200}
      closeDelay={100}
      onOpenChange={adaptivePlacement ? onOpenChange : undefined}
    >
      <HoverCardTrigger asChild>
        {/* Radix anchors to the trigger's border box. The row spans the
            column so long names wrap at the card width. Under side="bottom"
            that left edge never moves; adaptive placement still needs the
            full column to measure the right edge. */}
        <p ref={rowRef} className={FACESHEET_CARD_ROW_CLASS}>
          <TextLink
            to={`${participant.relativeUrl}/medications/${medication.id}`}
          >
            {label}
          </TextLink>
          {date && (
            <span className="select-none">
              <DotSeparator />
              <span className="text-stone-500">
                {formatDateNumberShort(date)}
              </span>
            </span>
          )}
        </p>
      </HoverCardTrigger>
      <HoverCardContent
        ref={adaptivePlacement ? cardRef : undefined}
        side={
          adaptivePlacement ? adaptiveSide : viewportLayout ? 'bottom' : 'right'
        }
        sideOffset={adaptivePlacement ? adaptiveSideOffset : undefined}
        align="start"
        alignOffset={viewportLayout || adaptivePlacement ? undefined : -16}
        scrollChainAnchor={adaptivePlacement ? rowRef : undefined}
        // Pinned rather than shrink-to-fit, the way the observation card is.
        // This card's history lines grow after mount as practitioner names
        // resolve, and a card measured before that lands seated for the narrow
        // width, where the viewport clamp then caps it and the arriving text
        // reflows into a column it will never grow out of. A fixed width is
        // correct from the first frame. Must equal MEDICATION_HOVER_CARD_WIDTH.
        className={cn(adaptivePlacement ? 'w-[610px]' : 'w-auto')}
      >
        <MedicationHistoryTooltip
          medicationDisplay={label}
          medicationRequests={medication.medicationRequests}
          actionRequestId={medication.id}
          onDraftPrescription={onDraftPrescription}
          onOpenDraftPrescription={(resolvedDraftId, medication) => {
            onOpenDraftPrescription(resolvedDraftId, medication)
          }}
          draftId={draftId}
          isCreatingDraft={isCreatingDraft}
        />
      </HoverCardContent>
    </HoverCard>
  )
}
